import { Box, Card, CircularProgress, type SxProps, type Theme, Typography } from '@mui/material'
import { ProgressReportCard } from './ProgressReportCard'
import { useNavigate, useParams } from 'react-router-dom'
import { handleApiResponse } from '../../../../utils/apiHelpers'
import { GetSurvey } from '../../../../services/SurveyService'
import { useEffect, useState } from 'react'
import { useProgressReportAccess } from '../../../../hooks/use-progressReportAccess'

const styles: Record<string, SxProps<Theme>> = {
  card: {
    backgroundColor: 'var(--grey-100)',
    borderRadius: '12px',
    padding: '20px 40px 40px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    position: 'relative',
    overflow: 'visible'
  },
  columnBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1em'
  }
}

export const OrgProgressReports = (): JSX.Element => {
  const { planOrgId, planId, rcnId } = useParams()
  const planOrgIdNum = Number(planOrgId)
  const annualPlanId = Number(planId)
  const rcnIdNum = Number(rcnId)

  const navigate = useNavigate()

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)

  const { isLoading: isLoadingAccess, surveyType, hasAccess, permissions } = useProgressReportAccess({
    annualPlanId,
    planOrgId: planOrgIdNum,
    rcnId: rcnIdNum,
    redirectPath: '..'
  })

  const canEdit = permissions.includes('edit')

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      if (!hasAccess || surveyType == null) return

      const survey = await handleApiResponse(GetSurvey(surveyType, planOrgIdNum))
      if (survey != null) {
        setIsSubmitted(survey.isSubmitted ?? false)
      }
      setLoading(false)
    }

    void loadData()
  }, [hasAccess, surveyType, annualPlanId, planOrgIdNum])

  if (isLoadingAccess) {
    return <CircularProgress sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
  }

  return (
    <Card sx={ styles.card }
      data-testid='progressReportsCard'
    >
      {loading
        ? <CircularProgress sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
        : <Box sx={ styles.columnBox }>
          <Typography variant='h1'>Progress Reports</Typography>

          <Typography variant='body1'>
            Share information about your progress toward your Implementation Plan with START at Mid-Year and End of Year by completing the Progress Reports.
          </Typography>

          <ProgressReportCard
            title={'Mid-Year Progress Report'}
            date='03/02/2026'
            description={'Complete the Mid-Year Progress Report to share your progress toward your Implementation Plan with START.'}
            buttonText={isSubmitted ? 'View Summary' : (canEdit ? 'Complete Report' : 'In Progress')}
            disabled={!isSubmitted && !canEdit}
            onButtonClick={() => { navigate(isSubmitted ? 'MidYear/Summary' : 'MidYear', { state: { surveyType } }) }}
            testId='midYearReport'
          />

          <ProgressReportCard
            title='End of Year Progress Report'
            date='05/05/2026'
            description='Complete the End of Year Progress Report to share your progress toward your Implementation Plan with START.'
            onButtonClick={() => { console.log(`End of Year for org: ${planOrgId ?? '0'}, ${planId ?? '0'}`) }}
            testId='endYearReport'
            comingSoon={true}
          />
        </Box>
      }
    </Card>
  )
}
